import RichTextField from './RichTextField';
import React, { useEffect, useState } from 'react';
import { Loader2, Send, RotateCcw, Info, ChevronRight, AlertTriangle, CheckCircle2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const EmailTemplatesAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState('');
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState('edit'); // edit | preview

  useEffect(() => {
    api.get('/admin/email-templates').then(r => {
      setTemplates(r.data);
      if (r.data.length && !activeKey) setActiveKey(r.data[0].key);
    }).catch(() => toast.error('Failed to load templates'));
    api.get('/admin/email/status').then(r => setStatus(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    api.get(`/admin/email-templates/${activeKey}`).then(r => {
      setTpl(r.data);
      setSubject(r.data.subject || '');
      setHtml(r.data.html || '');
    });
  }, [activeKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${activeKey}`, { subject, html });
      toast.success('Template saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/admin/email-templates/${activeKey}/test`);
      if (r.data.ok) toast.success(r.data.note);
      else toast.error(r.data.result?.error || 'Send failed');
      // Refresh status to surface last error
      api.get('/admin/email/status').then(x => setStatus(x.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setTesting(false); }
  };

  const insertPlaceholder = (ph) => {
    // Textarea is gone (now a rich-text editor). Append the token to the
    // current HTML value — user can drag it wherever they need.
    setHtml(h => (h || '') + `{{${ph}}}`);
  };

  return (
    <div className="space-y-4" data-testid="admin-email-templates">
      <div>
        <h2 className="text-xl font-black text-[#12263A]">Email Templates</h2>
        <p className="text-xs text-neutral-600 mt-1">Edit the emails Dr Tools sends via Resend. Placeholders like <code className="bg-neutral-100 px-1 py-0.5 rounded">{'{{name}}'}</code> are replaced at send time.</p>
      </div>

      {/* Status banner */}
      <ResendStatusBanner status={status} />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Template list */}
        <aside className="bg-white border border-neutral-200 rounded-sm p-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 px-2 pt-1 pb-2">Templates</div>
          <ul className="space-y-1">
            {templates.map(t => (
              <li key={t.key}>
                <button
                  onClick={() => setActiveKey(t.key)}
                  data-testid={`tpl-item-${t.key}`}
                  className={`w-full flex items-center justify-between text-left px-2.5 py-2 rounded-sm text-sm ${activeKey === t.key ? 'bg-[#F46A25] text-white' : 'hover:bg-neutral-100 text-[#12263A]'}`}
                >
                  <span className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> {t.label}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Editor */}
        <div className="md:col-span-3 bg-white border border-neutral-200 rounded-sm p-4">
          {!tpl ? (
            <div className="flex items-center justify-center py-10 text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-black text-[#12263A]">{tpl.label}</h3>
                  <p className="text-xs text-neutral-600 mt-0.5">{tpl.description}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setTab('edit')} className={`text-xs font-bold px-3 py-1.5 rounded-sm ${tab === 'edit' ? 'bg-[#12263A] text-white' : 'bg-neutral-100 text-[#12263A]'}`} data-testid="tpl-tab-edit">Edit</button>
                  <button onClick={() => setTab('preview')} className={`text-xs font-bold px-3 py-1.5 rounded-sm ${tab === 'preview' ? 'bg-[#12263A] text-white' : 'bg-neutral-100 text-[#12263A]'}`} data-testid="tpl-tab-preview">Preview</button>
                </div>
              </div>

              {tab === 'edit' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11px] uppercase tracking-wide font-bold text-neutral-500 mb-1">Placeholders</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(tpl.placeholders || []).map(ph => (
                        <button
                          key={ph}
                          type="button"
                          onClick={() => insertPlaceholder(ph)}
                          className="text-[11px] bg-orange-50 hover:bg-orange-100 border border-orange-200 text-[#F46A25] font-mono rounded-sm px-2 py-0.5"
                          data-testid={`tpl-placeholder-${ph}`}
                        >
                          {`{{${ph}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wide font-bold text-neutral-500 mb-1">Subject</label>
                    <input
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      data-testid="tpl-subject-input"
                      className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm font-medium focus:outline-none focus:border-[#F46A25]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wide font-bold text-neutral-500 mb-1">Body</label>
                    <RichTextField value={html} onSave={setHtml} rows={18} />
                    <p className="text-[11px] text-neutral-500 mt-1 flex items-start gap-1">
                      <Info className="w-3 h-3 mt-0.5" />
                      Use the toolbar to bold text, change size, add links. Insert placeholders like <code>{'{{name}}'}</code> using the buttons above.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-200">
                    <button
                      onClick={save}
                      disabled={saving}
                      data-testid="tpl-save-btn"
                      className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs px-4 py-2 rounded-sm disabled:opacity-60 flex items-center gap-1"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Save template
                    </button>
                    <button
                      onClick={sendTest}
                      disabled={testing}
                      data-testid="tpl-send-test-btn"
                      className="bg-[#12263A] hover:bg-[#0e1c2d] text-white font-bold text-xs px-4 py-2 rounded-sm disabled:opacity-60 flex items-center gap-1"
                    >
                      {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test to me
                    </button>
                    <button
                      onClick={() => {
                        if (!window.confirm('Reset this template back to the built-in default?')) return;
                        // Fetch default by clearing edit state — re-request template after delete.
                        api.delete(`/admin/email-templates/${activeKey}`).catch(() => {});
                        api.get(`/admin/email-templates/${activeKey}`).then(r => {
                          setSubject(r.data.subject); setHtml(r.data.html);
                          toast.success('Reset to default');
                        });
                      }}
                      data-testid="tpl-reset-btn"
                      className="border border-neutral-300 hover:border-red-300 hover:text-red-600 text-neutral-600 font-semibold text-xs px-4 py-2 rounded-sm flex items-center gap-1"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Reset to default
                    </button>
                  </div>
                </div>
              )}

              {tab === 'preview' && (
                <div className="border border-neutral-200 rounded-sm overflow-hidden">
                  <div className="bg-neutral-50 border-b border-neutral-200 px-3 py-2 text-xs text-neutral-600">
                    <b>Subject:</b> {subject.replace(/{{brand_name}}/g, 'Dr Tools').replace(/{{order_id}}/g, 'TEST0001')}
                  </div>
                  <iframe
                    title="preview"
                    srcDoc={html.replace(/{{brand_name}}/g, 'Dr Tools')}
                    className="w-full min-h-[500px] bg-white"
                    data-testid="tpl-preview-iframe"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ResendStatusBanner = ({ status }) => {
  const [audienceInput, setAudienceInput] = useState('');
  const [savingAudience, setSavingAudience] = useState(false);
  useEffect(() => { setAudienceInput(status?.audience_id || ''); }, [status?.audience_id]);

  if (!status) return null;
  const hasKey = status.api_key_set;
  const err = status.last_error;

  const saveAudience = async () => {
    setSavingAudience(true);
    try {
      await api.put('/admin/settings', { resend_audience_id: audienceInput.trim() });
      toast.success('Audience ID saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingAudience(false); }
  };

  const audienceRow = (
    <div className="flex items-center gap-2 pt-2 border-t border-current/10 mt-2">
      <label className="text-[11px] font-bold uppercase tracking-wide">Customers audience ID</label>
      <input
        value={audienceInput}
        onChange={e => setAudienceInput(e.target.value)}
        placeholder="paste from resend.com/audiences"
        data-testid="resend-audience-input"
        className="flex-1 border border-neutral-300 rounded-sm px-2 py-1 text-xs font-mono bg-white text-[#12263A]"
      />
      <button
        onClick={saveAudience}
        disabled={savingAudience}
        data-testid="resend-audience-save"
        className="bg-[#12263A] text-white text-xs font-bold px-3 py-1.5 rounded-sm disabled:opacity-60"
      >
        {savingAudience ? '…' : 'Save'}
      </button>
    </div>
  );

  if (hasKey && !err) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-800" data-testid="resend-status-ok">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <b>Resend is configured.</b> Sender: <code>{status.sender_email}</code>. Audience:{' '}
            {status.audience_id ? <code>{status.audience_id}</code> : <span className="text-amber-700">not set (newsletter opt-ins will not sync)</span>}.
          </div>
        </div>
        {audienceRow}
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-xs text-amber-900" data-testid="resend-status-warn">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          {!hasKey && <div><b>RESEND_API_KEY not set.</b> Emails will not send.</div>}
          {err && (
            <div className="mt-1">
              <b>Last error ({err.context}):</b> {err.message}
              <div className="mt-1 text-[10px] text-amber-800">
                Most common causes: (1) sender domain not verified in Resend — add it at{' '}
                <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>;
                (2) API key restricted to sending only — recreate with <b>full access</b> at{' '}
                <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">resend.com/api-keys</a>.
              </div>
            </div>
          )}
        </div>
      </div>
      {audienceRow}
    </div>
  );
};

export default EmailTemplatesAdmin;
